import { useState, useRef } from "react";
import { Clock, User, Phone, CalendarDays, NotebookPen } from "lucide-react";
import { useStore } from "../store";
import { api } from "../api";
import {
  dateKey,
  localStart,
  localTime,
  formatTime,
  overlaps,
  durationLabel,
  appointmentSchema,
  MAX_NOTES_LENGTH,
  type Appointment,
  type Client,
} from "../../shared/domain";
import { AppointmentDelete } from "./AppointmentDelete";
import { Modal, ErrorText } from "./ui";
export function AppointmentForm({
  appointment,
  client,
  date,
  onClose,
  onDelete,
}: {
  appointment?: Appointment;
  client?: Client;
  date: string;
  onClose: () => void;
  onDelete?: (a: Appointment, until: number) => void;
}) {
  const { data, refresh, offline } = useStore();
  const s = data!.settings;
  const submission = useRef(false);
  const initial =
    client || data!.clients.find((c) => c.id === appointment?.client_id);
  const [name, setName] = useState(initial?.name || ""),
    [phone, setPhone] = useState(initial?.phone || ""),
    [clientId, setClientId] = useState<string | undefined>(initial?.id),
    [day, setDay] = useState(
      appointment ? dateKey(appointment.start, s.timezone) : date,
    ),
    [time, setTime] = useState(
      appointment ? localTime(appointment.start, s.timezone) : "10:00",
    ),
    [duration, setDuration] = useState(
      appointment?.duration || s.defaultDuration,
    ),
    [notes, setNotes] = useState(appointment?.notes ?? ""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [suggest, setSuggest] = useState(false);
  let start: number | null = null;
  try {
    start = localStart(day, time, s.timezone);
  } catch {
    /* Invalid or ambiguous local time is shown below. */
  }
  const conflict =
    start === null
      ? null
      : data!.appointments.find(
          (a) =>
            a.id !== appointment?.id &&
            overlaps({ start: start!, duration }, a),
        );
  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (offline || submission.current) return;
    if (start === null) {
      setError(
        "This local time does not exist or occurs twice. Choose another time.",
      );
      return;
    }
    if (conflict) {
      setError(
        `Overlaps ${conflict.name} at ${formatTime(conflict.start, s)}.`,
      );
      return;
    }
    const input = appointmentSchema.safeParse({
      name,
      phone,
      clientId,
      start,
      duration,
      notes,
    });
    if (!input.success) {
      setError(input.error.issues[0].message);
      return;
    }
    submission.current = true;
    setBusy(true);
    try {
      await api(
        appointment ? "/appointments/" + appointment.id : "/appointments",
        appointment ? "PUT" : "POST",
        input.data,
      );
      await refresh();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      submission.current = false;
      setBusy(false);
    }
  }
  return (
    <Modal
      title={appointment ? "Edit appointment" : "New appointment"}
      onClose={onClose}
    >
      <form onSubmit={save} className="form">
        <fieldset disabled={offline || busy}>
          <label>
            <span>
              <User size={16} />
              Client name
            </span>
            <input
              required
              autoFocus
              value={name}
              onFocus={() => setSuggest(true)}
              onChange={(e) => {
                setName(e.target.value);
                setClientId(undefined);
                setSuggest(true);
              }}
              placeholder="Who is coming in?"
              maxLength={120}
            />
          </label>
          {suggest && !clientId && name && (
            <div className="suggestions">
              {data!.clients
                .filter((c) =>
                  c.name.toLowerCase().includes(name.toLowerCase()),
                )
                .slice(0, 5)
                .map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => {
                      setClientId(c.id);
                      setName(c.name);
                      setPhone(c.phone);
                      setSuggest(false);
                    }}
                  >
                    {c.name}
                    <small>{c.phone || "No phone number"}</small>
                  </button>
                ))}
              <p>
                A new client will be created unless you select an existing one.
              </p>
            </div>
          )}
          <label>
            <span>
              <Phone size={16} />
              Phone number <small>optional</small>
            </span>
            <input
              type="tel"
              value={phone}
              readOnly={!!clientId}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+961"
              maxLength={40}
            />
          </label>
          {clientId && (
            <small className="muted">
              Saved client selected. Edit their phone in Clients.
            </small>
          )}
          <div className="form-row">
            <label>
              <span>
                <CalendarDays size={16} />
                Date
              </span>
              <input
                type="date"
                required
                value={day}
                onChange={(e) => setDay(e.target.value)}
              />
            </label>
            <label>
              <span>
                <Clock size={16} />
                Start time
              </span>
              <input
                type="time"
                required
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </label>
          </div>
          <div className="duration-heading">
            <span>Duration</span>
            <strong>{durationLabel(duration) || "Choose duration"}</strong>
          </div>
          <div className="form-row">
            <label>
              <span>Hours</span>
              <input
                aria-label="Duration hours"
                type="number"
                min="0"
                max="168"
                value={Math.floor(duration / 60)}
                onChange={(e) =>
                  setDuration(Number(e.target.value) * 60 + (duration % 60))
                }
              />
            </label>
            <label>
              <span>Minutes</span>
              <input
                aria-label="Duration minutes"
                type="number"
                min="0"
                max="59"
                step="1"
                value={duration % 60}
                onChange={(e) =>
                  setDuration(
                    Math.floor(duration / 60) * 60 + Number(e.target.value),
                  )
                }
              />
            </label>
          </div>
          <div className="duration-options">
            {[30, 60, 90, 150].map((m) => (
              <button
                type="button"
                className={duration === m ? "selected" : ""}
                key={m}
                onClick={() => setDuration(m)}
              >
                {durationLabel(m)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setDuration(Math.min(10080, duration + 15))}
            >
              +15m
            </button>
          </div>
          <div className="end-time">
            {start !== null
              ? `Ends at ${formatTime(start + duration * 60000, s)}${dateKey(start + duration * 60000, s.timezone) !== day ? " · " + dateKey(start + duration * 60000, s.timezone) : ""}`
              : "Choose a valid local time"}
            <small>{s.timezone}</small>
          </div>
          <label>
            <span>
              <NotebookPen size={16} />
              Notes <small>optional</small>
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={MAX_NOTES_LENGTH}
              rows={4}
              placeholder="Anything to remember for this appointment"
            />
            <small className="muted">
              {notes.length.toLocaleString()} / 5,000
            </small>
          </label>
          {conflict && (
            <ErrorText
              text={`Overlaps ${conflict.name} at ${formatTime(conflict.start, s)}`}
            />
          )}
          <ErrorText text={error} />
          <button
            className="primary wide"
            disabled={!!conflict || !duration || start === null}
          >
            {busy
              ? "Saving…"
              : appointment
                ? "Update appointment"
                : "Save appointment"}
          </button>
          {appointment && (
            <AppointmentDelete
              appointment={appointment}
              onClose={onClose}
              onDelete={onDelete}
              disabled={busy}
            />
          )}
        </fieldset>
      </form>
    </Modal>
  );
}
